// 2026-05-26 - FormuLearning - Step 5 §5.4 분석 결과 차트
// 파일 위치: frontend/src/features/step5/AnalysisSection.tsx
// 2026-11-03 수정 - 이슈 #114: viewer 는 Run Analysis 비활성(결과·그래프 보기는 그대로).
// 2026-09-23 수정 - 이슈 #85: 서버가 준 그래프 라벨(Mixture — Trace Plot·Cox 효과)을 쓰고, 정의되지 않은 R²·Q²(null)는 «—».

import { useEffect, useRef } from 'react'
import type { AnalysisResult, ChartLabel, GraphData } from '../../api/step5'
import { useUiStore } from '../../stores/uiStore'
import { chartTokens } from '../../lib/chartTheme'
import { useCanWrite, READ_ONLY_TITLE } from '../../hooks/useCanWrite'

interface Props {
  analysisResult: AnalysisResult
  graphData: GraphData
  onAnalyze: () => Promise<void>
  analyzing: boolean
}

const PLOT_OPTS = { displayModeBar: false, responsive: true } as const

// 2026-06-19 - 테마(이슈 #9) 기준 공통 레이아웃·그리드 (현재 data-theme의 CSS 변수에서 읽음)
function layoutBase() {
  const t = chartTokens()
  return {
    paper_bgcolor: 'transparent',
    plot_bgcolor: t.plotBg,
    font: { color: t.font, size: 10 },
  }
}
function gridAxis() {
  return { gridcolor: chartTokens().grid }
}

function PlotlyChart({ deps, render }: { deps: unknown[]; render: (el: HTMLDivElement, Plotly: typeof import('plotly.js-dist-min')) => void }) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!ref.current) return
    let cancelled = false
    import('plotly.js-dist-min').then((Plotly) => {
      if (cancelled || !ref.current) return
      render(ref.current, Plotly)
    })
    return () => { cancelled = true }
  }, deps)
  return <div ref={ref} style={{ width: '100%', height: 280 }} />
}

function ParetoChart({ pareto, label }: { pareto: unknown[]; label?: ChartLabel }) {
  const theme = useUiStore((s) => s.theme)
  if (!pareto?.length) return null
  const items = pareto as { factor?: string; effect?: number }[]
  if (!items.every((p) => typeof p.factor === 'string' && typeof p.effect === 'number')) return null

  return (
    <PlotlyChart deps={[pareto, theme, label]} render={(el, Plotly) => {
      const t = chartTokens()
      Plotly.newPlot(el, [{
        x: items.map((p) => p.effect!),
        y: items.map((p) => p.factor!),
        type: 'bar',
        orientation: 'h',
        marker: { color: t.teal },
      }], {
        ...layoutBase(),
        margin: { t: 30, r: 15, b: 35, l: 80 },
        xaxis: { ...gridAxis(), title: { text: label?.x ?? '|t-value|' } },
        yaxis: { ...gridAxis() },
        title: { text: label?.title ?? 'Pareto Chart', font: { size: 11, color: t.font } },
      }, PLOT_OPTS)
    }} />
  )
}

function ContourChart({ data }: { data: unknown[] }) {
  const theme = useUiStore((s) => s.theme)
  if (!data?.length) return null
  const item = data[0] as { x: number[]; y: number[]; z: number[][]; x_factor: string; y_factor: string }
  if (!item.x || !item.y || !item.z) return null

  return (
    <PlotlyChart deps={[data, theme]} render={(el, Plotly) => {
      const t = chartTokens()
      Plotly.newPlot(el, [{
        x: item.x,
        y: item.y,
        z: item.z,
        type: 'contour',
        colorscale: [[0, '#1a1b3a'], [0.5, '#9b8afb'], [1, '#4fd1c5']],
        contours: { coloring: 'heatmap' },
        colorbar: { tickfont: { color: t.font, size: 9 }, len: 0.8 },
      }], {
        ...layoutBase(),
        margin: { t: 30, r: 15, b: 45, l: 55 },
        xaxis: { ...gridAxis(), title: { text: item.x_factor } },
        yaxis: { ...gridAxis(), title: { text: item.y_factor } },
        title: { text: 'Contour Plot', font: { size: 11, color: t.font } },
      }, PLOT_OPTS)
    }} />
  )
}

function ResponseSurfaceChart({ data }: { data: unknown[] }) {
  const theme = useUiStore((s) => s.theme)
  if (!data?.length) return null
  const item = data[0] as { x: number[]; y: number[]; z: number[][]; x_factor: string; y_factor: string }
  if (!item.x || !item.y || !item.z) return null

  return (
    <PlotlyChart deps={[data, theme]} render={(el, Plotly) => {
      const t = chartTokens()
      Plotly.newPlot(el, [{
        x: item.x,
        y: item.y,
        z: item.z,
        type: 'surface',
        colorscale: [[0, '#1a1b3a'], [0.5, '#9b8afb'], [1, '#4fd1c5']],
        colorbar: { tickfont: { color: t.font, size: 9 }, len: 0.8 },
      }], {
        ...layoutBase(),
        margin: { t: 30, r: 15, b: 45, l: 55 },
        scene: {
          xaxis: { title: { text: item.x_factor }, ...gridAxis(), backgroundcolor: 'transparent' },
          yaxis: { title: { text: item.y_factor }, ...gridAxis(), backgroundcolor: 'transparent' },
          zaxis: { title: { text: 'Response' }, ...gridAxis(), backgroundcolor: 'transparent' },
          bgcolor: 'transparent',
        },
        title: { text: '3D Response Surface', font: { size: 11, color: t.font } },
      }, PLOT_OPTS)
    }} />
  )
}

function MainEffectChart({ data, label }: { data: unknown[]; label?: ChartLabel }) {
  const theme = useUiStore((s) => s.theme)
  if (!data?.length) return null
  const items = data as { x: number[]; y: number[]; factor: string }[]
  if (!items.every((d) => d.x && d.y && d.factor)) return null
  const colors = ['#9b8afb', '#4fd1c5', '#f6ad55', '#fc8181']

  return (
    <PlotlyChart deps={[data, theme, label]} render={(el, Plotly) => {
      const t = chartTokens()
      Plotly.newPlot(el, items.map((d, i) => ({
        x: d.x,
        y: d.y,
        type: 'scatter' as const,
        mode: 'lines' as const,
        name: d.factor,
        line: { color: colors[i % colors.length], width: 2 },
      })), {
        ...layoutBase(),
        margin: { t: 30, r: 15, b: 40, l: 55 },
        xaxis: { ...gridAxis(), title: { text: label?.x ?? 'Factor Level' } },
        yaxis: { ...gridAxis(), title: { text: 'Predicted Response' } },
        title: { text: label?.title ?? 'Main Effects', font: { size: 11, color: t.font } },
        showlegend: true,
        legend: { font: { size: 9, color: t.font } },
      }, PLOT_OPTS)
    }} />
  )
}

function InteractionChart({ data }: { data: unknown[] }) {
  const theme = useUiStore((s) => s.theme)
  if (!data?.length) return null
  const item = data[0] as { x: number[]; lines: { y: number[]; label: string }[]; x_factor: string; trace_factor: string }
  if (!item.x || !item.lines) return null
  const colors = ['#9b8afb', '#4fd1c5', '#f6ad55', '#fc8181']

  return (
    <PlotlyChart deps={[data, theme]} render={(el, Plotly) => {
      const t = chartTokens()
      Plotly.newPlot(el, item.lines.map((line, i) => ({
        x: item.x,
        y: line.y,
        type: 'scatter' as const,
        mode: 'lines' as const,
        name: `${item.trace_factor} = ${line.label}`,
        line: { color: colors[i % colors.length], width: 2, dash: i === 1 ? 'solid' : 'dash' },
      })), {
        ...layoutBase(),
        margin: { t: 30, r: 15, b: 40, l: 55 },
        xaxis: { ...gridAxis(), title: { text: item.x_factor } },
        yaxis: { ...gridAxis(), title: { text: 'Predicted Response' } },
        title: { text: 'Interaction Plot', font: { size: 11, color: t.font } },
        showlegend: true,
        legend: { font: { size: 9, color: t.font } },
      }, PLOT_OPTS)
    }} />
  )
}

export default function AnalysisSection({ analysisResult, graphData, onAnalyze, analyzing }: Props) {
  const canWrite = useCanWrite()
  const hasAnalysis = analysisResult?.anova?.length > 0
  const hasContour = graphData.contour?.length > 0
  const hasMainEffect = graphData.main_effect?.length > 0
  const hasInteraction = graphData.interaction?.length > 0
  const hasResponseSurface = graphData.response_surface?.length > 0
  const hasPareto = graphData.pareto?.length > 0

  return (
    <div className="mb-4">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-text-secondary">
          Analysis
        </h4>
        <button
          onClick={onAnalyze}
          disabled={analyzing || !canWrite}
          title={canWrite ? undefined : READ_ONLY_TITLE}
          className="rounded bg-accent-purple px-3 py-1.5 text-[11px] font-semibold text-[#1a1b1e] transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {analyzing ? 'Analyzing...' : 'Run Analysis'}
        </button>
      </div>

      {hasAnalysis ? (
        <>
          <div className="mb-3 flex gap-4">
            <div className="rounded border px-3 py-2 text-center" style={{ borderColor: 'var(--border-teal)', backgroundColor: 'var(--bg-chip-teal)' }}>
              <p className="text-[10px] text-text-secondary">R²</p>
              <p className="text-sm font-semibold text-accent-teal">{analysisResult.r_squared == null ? '—' : analysisResult.r_squared.toFixed(3)}</p>
            </div>
            <div className="rounded border px-3 py-2 text-center" style={{ borderColor: 'var(--border-teal)', backgroundColor: 'var(--bg-chip-teal)' }}>
              <p className="text-[10px] text-text-secondary">Q²</p>
              <p className="text-sm font-semibold text-accent-teal">{analysisResult.q_squared == null ? '—' : analysisResult.q_squared.toFixed(3)}</p>
            </div>
          </div>

          <div className="mb-3 overflow-x-auto rounded border" style={{ borderColor: 'var(--border-subtle)' }}>
            <table className="w-full text-[11px]">
              <thead>
                <tr style={{ backgroundColor: 'var(--color-bg-panel)' }}>
                  <th className="px-2 py-1.5 text-left font-medium text-text-secondary">Source</th>
                  <th className="px-2 py-1.5 text-center font-medium text-text-secondary">DF</th>
                  <th className="px-2 py-1.5 text-center font-medium text-text-secondary">SS</th>
                  <th className="px-2 py-1.5 text-center font-medium text-text-secondary">MS</th>
                  <th className="px-2 py-1.5 text-center font-medium text-text-secondary">F</th>
                  <th className="px-2 py-1.5 text-center font-medium text-text-secondary">p-value</th>
                </tr>
              </thead>
              <tbody>
                {analysisResult.anova.map((row, i) => (
                  <tr key={i} className="border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                    <td className="px-2 py-1.5 font-medium text-text-body">{row.source}</td>
                    <td className="px-2 py-1.5 text-center text-text-body">{row.df}</td>
                    <td className="px-2 py-1.5 text-center text-text-body">{row.ss != null ? row.ss.toFixed(2) : '—'}</td>
                    <td className="px-2 py-1.5 text-center text-text-body">{row.ms != null ? row.ms.toFixed(2) : '—'}</td>
                    <td className="px-2 py-1.5 text-center text-text-body">{row.f_value != null ? row.f_value.toFixed(2) : '—'}</td>
                    <td className="px-2 py-1.5 text-center" style={{ color: row.p_value != null && row.p_value < 0.05 ? 'var(--color-accent-teal)' : 'var(--color-text-body)' }}>
                      {row.p_value == null ? '—' : row.p_value < 0.001 ? '<0.001' : row.p_value.toFixed(3)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            {hasContour && (
              <div className="rounded border p-2" style={{ borderColor: 'var(--border-subtle)' }}>
                <ContourChart data={graphData.contour} />
              </div>
            )}
            {hasResponseSurface && (
              <div className="rounded border p-2" style={{ borderColor: 'var(--border-subtle)' }}>
                <ResponseSurfaceChart data={graphData.response_surface} />
              </div>
            )}
            {hasMainEffect && (
              <div className="rounded border p-2" style={{ borderColor: 'var(--border-subtle)' }}>
                <MainEffectChart data={graphData.main_effect} label={graphData.labels?.main_effect} />
              </div>
            )}
            {hasInteraction && (
              <div className="rounded border p-2" style={{ borderColor: 'var(--border-subtle)' }}>
                <InteractionChart data={graphData.interaction} />
              </div>
            )}
            {hasPareto && (
              <div className="rounded border p-2" style={{ borderColor: 'var(--border-subtle)' }}>
                <ParetoChart pareto={graphData.pareto} label={graphData.labels?.pareto} />
              </div>
            )}
          </div>
        </>
      ) : (
        <p className="text-[11px] text-text-secondary">Enter Y values in the case table, then click "Run Analysis".</p>
      )}
    </div>
  )
}
